import usePreviousHook from 'containers/App/hooks/usePrevious';
import { get, omit } from 'lodash';
import { useSelector as reduxUseSelector } from 'react-redux';

import { unMapNodesAsQuestions } from 'containers/QuestionnairePage/utils';
import {
  makeRequiresManualSaveQuestionnaire,
  makeSelectShouldSendDisqualificationRulesOnSave,
} from 'containers/Survey/selectors';
import { makeSelectIsPeeq } from 'containers/Survey/shared-selectors';
import {
  makeSelectUpdateCostVersion,
  makeSelectFormattedNodes,
  makeSelectGroupsShuffling,
  makeSelectQuestionnaireEditVersion,
  makeSelectIsQuestionnairePreview,
  makeSelectSurveyLogicToStore,
  makeSelectMaxQlPath,
  makeSelectHasFetched,
  makeSelectSurveyId,
} from 'containers/QuestionnairePage/selectors';
import { makeSelectHiddenVariables } from 'modules/HiddenVariables/selectors';
import { makeSelectDisqualificationLogicToStore } from 'containers/QuestionnaireLogic/selectors';
import { makeSelectQuestionQuotasMinimalMap } from 'containers/QuestionnairePage/selectorUtils/quotasMinimal';

let questionnaireData;
let previousSurveyId;
export function useGetQuestionnaireData(
  { surveyId },
  { useSelector = reduxUseSelector, usePrevious = usePreviousHook } = {},
) {
  const isPeeq = useSelector(makeSelectIsPeeq);
  const questions = useSelector(makeSelectFormattedNodes);
  const questionnaireEditVersion = useSelector(
    makeSelectQuestionnaireEditVersion,
  );

  const isQuestionnairePreview = useSelector(makeSelectIsQuestionnairePreview);
  const surveyLogicToStore = useSelector(makeSelectSurveyLogicToStore);
  const questionGroupShuffle = useSelector(makeSelectGroupsShuffling);
  const requiresManualSaveQuestionnaire = useSelector(
    makeRequiresManualSaveQuestionnaire,
  );
  const updateCostVersion = useSelector(makeSelectUpdateCostVersion);

  const disqualificationLogicToStore = useSelector(
    makeSelectDisqualificationLogicToStore,
  );
  const maxQlPath = useSelector(makeSelectMaxQlPath);

  const prevQuestionnaireEditVersion = usePrevious(questionnaireEditVersion);
  const previousMaxQlPath = usePrevious(maxQlPath);

  const shouldSendDisqualificationRules = useSelector(
    makeSelectShouldSendDisqualificationRulesOnSave,
  );

  const hasFetched = useSelector(makeSelectHasFetched);

  const hiddenVariables = useSelector(makeSelectHiddenVariables);
  const quotaMinimalMap = useSelector(makeSelectQuestionQuotasMinimalMap);

  // If the survey id in store doesn't match the URL's survey id,
  // the hook returns shouldUpdateQuestionnaire: false,
  // blocking the save and preventing one survey's questionnaire
  // from being written to another survey's questionnaire.
  // https://prodege.atlassian.net/browse/RES-12600
  const surveyIdFromStore = useSelector(makeSelectSurveyId);
  if (!hasFetched || (surveyIdFromStore && surveyIdFromStore !== surveyId)) {
    return { shouldUpdateQuestionnaire: false };
  }

  if (previousSurveyId !== surveyId) {
    questionnaireData = undefined;
    previousSurveyId = surveyId;
  }

  const shouldUpdateByVersionUpdate =
    questionnaireEditVersion !== prevQuestionnaireEditVersion &&
    questionnaireEditVersion !== -1 &&
    prevQuestionnaireEditVersion !== -1;

  const shouldUpdateQuestionnaire =
    shouldUpdateByVersionUpdate &&
    !requiresManualSaveQuestionnaire &&
    !isQuestionnairePreview;

  const shouldUpdateQuestionnaireManually =
    requiresManualSaveQuestionnaire && shouldUpdateByVersionUpdate;

  if (!shouldUpdateQuestionnaire && questionnaireData) {
    return {
      ...questionnaireData,
      shouldUpdateQuestionnaire,
      shouldUpdateQuestionnaireManually,
      costAffected: false,
      version: questionnaireEditVersion,
      previousVersion: prevQuestionnaireEditVersion,
      questionGroupShuffle,
    };
  }

  const logicChanged = questionnaireEditVersion === updateCostVersion;
  const costAffected =
    logicChanged ||
    previousMaxQlPath !== maxQlPath ||
    // this (isPeeq) is required for peeq because the cost is affected by
    // the LOI which in turn is affected by the length of the texts/answers etc
    // If we can find a quick way to identify that the changes to the
    // questionnaire do not affect the LOI, we can use that flag instead
    isPeeq;

  return generateNewQuestionnaireData({
    questionnaireEditVersion,
    surveyLogicToStore,
    questions,
    prevQuestionnaireEditVersion,
    questionGroupShuffle,
    disqualificationLogicToStore,
    shouldSendDisqualificationRules,
    shouldUpdateQuestionnaire,
    shouldUpdateQuestionnaireManually,
    costAffected,
    hiddenVariables,
    quotaMinimalMap,
  });
}

export function generateNewQuestionnaireData({
  questionnaireEditVersion = -1,
  surveyLogicToStore = {},
  questions = [],
  prevQuestionnaireEditVersion = -1,
  questionGroupShuffle = 0,
  disqualificationLogicToStore = false,
  shouldSendDisqualificationRules = true,
  shouldUpdateQuestionnaire = false,
  shouldUpdateQuestionnaireManually = false,
  costAffected = false,
  hiddenVariables,
  quotaMinimalMap,
}) {
  const { newQuestions, newGroups } = unMapNodesAsQuestions(questions);

  const logicQuestions = get(surveyLogicToStore, 'logic.questions', []);
  const cleanLogicQuestions = logicQuestions.map((question) => ({
    ...question,
    // filter out answers that contain no refIds
    answers: question.answers.filter((answer) => answer.refId),
  }));

  const newQuestionsWithoutPristine = newQuestions.map((question) =>
    omit(question, ['pristineValues']),
  );

  questionnaireData = {
    questions: newQuestionsWithoutPristine,
    questionGroups: newGroups,
    costAffected,
    version: questionnaireEditVersion,
    previousVersion: prevQuestionnaireEditVersion,
    questionGroupShuffle,
    logic: {
      questions: cleanLogicQuestions,
      groups: newGroups.map((group) => group.refId),
    },
    ql: surveyLogicToStore.ql,
    disqualificationQl: shouldSendDisqualificationRules
      ? disqualificationLogicToStore
      : false,
    shouldUpdateQuestionnaire,
    shouldUpdateQuestionnaireManually,
    hiddenVariables,
    quotaMinimalMap,
  };
  return questionnaireData;
}

export default useGetQuestionnaireData;
